import type { SeatLayoutDefinition, SeatLayoutSection } from '@anjabom/core';
import { type CSSProperties, useMemo, useState } from 'react';
import { ExternalLink } from './ExternalLink';

export interface SeatMapSelection {
  sectionCode: string;
  rowLabel: string;
  seatNumber: number;
}

interface InteractiveSeatMapProps {
  layout: SeatLayoutDefinition;
  selection: SeatMapSelection | null;
  onSelectSeat(selection: SeatMapSelection): void;
  onSelectSection(sectionCode: string): void;
  onPreviewSection?(section: SeatLayoutSection): void;
  defaultEasy?: boolean;
  memories?: Array<{sectionCode:string;rowLabel:string;seatNumber:number;score:number|null}>;
}

export function InteractiveSeatMap({ layout, selection, onSelectSeat, onSelectSection, onPreviewSection, defaultEasy = false, memories = [] }: InteractiveSeatMapProps) {
  const [easy, setEasy] = useState(defaultEasy);
  const [floorId, setFloorId] = useState(layout.floors[0]?.id ?? '');
  const floor = useMemo(
    () => layout.floors.find((item) => item.id === floorId) ?? layout.floors[0],
    [floorId, layout.floors],
  );
  const [sectionId, setSectionId] = useState(floor?.sections[0]?.id ?? '');
  const section = floor?.sections.find((item) => item.id === sectionId) ?? floor?.sections[0];
  const effectivePrecision = section?.rows?.length ? 'seat' : layout.precision === 'booking' ? 'booking' : 'section';

  const chooseFloor = (nextFloorId: string) => {
    const nextFloor = layout.floors.find((item) => item.id === nextFloorId);
    setFloorId(nextFloorId);
    setSectionId(nextFloor?.sections[0]?.id ?? '');
    if (!nextFloor?.sections[0]?.rows) onSelectSection(nextFloor?.sections[0]?.label ?? nextFloor?.label ?? '');
  };

  const chooseSection = (nextSection: SeatLayoutSection) => {
    setSectionId(nextSection.id);
    onPreviewSection?.(nextSection);
    if (!nextSection.rows) onSelectSection(nextSection.label);
  };

  return (
    <section className="seat-map-card" aria-labelledby="seat-map-title">
      <header className="seat-map-head">
        <div>
          <div className="inline">
            <h2 id="seat-map-title">{layout.title}</h2>
            <span className={`map-precision map-precision-${effectivePrecision}`}>
              {effectivePrecision === 'seat' ? '시설 좌표 참고' : effectivePrecision === 'section' ? '구역까지 확인됨' : '회차에서 확인'}
            </span>
          </div>
          <p className="muted">{layout.note}</p>
        </div>
        <ExternalLink className="source-link" href={layout.source.url}>
          공식 원본 확인하기 <span aria-hidden="true">↗</span>
        </ExternalLink>
      </header>

      <label className="check-item"><input type="checkbox" checked={easy} onChange={(e)=>setEasy(e.target.checked)}/>이동 편한 자리 강조하기</label>
      {easy ? <p className="micro">공식 휠체어석·통로 옆·가로 통로 바로 뒤 열을 표시해요. 공연마다 판매 여부가 달라요.<ExternalLink href={layout.source.url}>공식 안내 확인하기</ExternalLink></p>:null}
      {layout.precision === 'booking' ? (
        <BookingSeatMap surfaceLabel={layout.surfaceLabel} />
      ) : (
        <>
          <div className="map-tabs" role="tablist" aria-label="층 선택">
            {layout.floors.map((item) => (
              <button
                className="map-tab"
                type="button"
                role="tab"
                aria-selected={item.id === floor?.id}
                key={item.id}
                onClick={() => chooseFloor(item.id)}
              >
                {item.label}{item.seatCount ? <span>{item.seatCount.toLocaleString('ko-KR')}석</span> : null}
              </button>
            ))}
          </div>

          {floor && floor.sections.length > 1 ? (
            <div className="map-tabs map-section-tabs" role="tablist" aria-label="구역 선택">
              {floor.sections.map((item) => (
                <button
                  className="map-tab"
                  type="button"
                  role="tab"
                  aria-selected={item.id === section?.id}
                  key={item.id}
                  onClick={() => chooseSection(item)}
                >
                  {item.shortLabel}{item.shortLabel?.endsWith('측') || item.shortLabel === '중앙' ? '' : '구역'}
                </button>
              ))}
            </div>
          ) : null}

          <div className="seat-map-shell">
            <div className={`venue-surface venue-surface-${layout.surfaceLabel === '스크린' ? 'screen' : 'stage'}`}>
              <span>{layout.surfaceLabel}</span>
            </div>
            <p className="map-selection-cue">{section?.rows ? '아래 좌석을 눌러 내 자리를 골라요.' : '아래 구역을 고른 뒤 티켓의 열·번호를 입력해요.'}</p>
            {section?.rows ? (
              <ExactSeatRows section={section} selection={selection} onSelectSeat={onSelectSeat} easy={easy} memories={memories} />
            ) : floor ? (
              <SectionBlocks floor={floor} selectedSectionId={section?.id ?? ''} onSelectSection={chooseSection} />
            ) : null}
          </div>

          <div className="seat-map-legend" aria-label="좌석도 범례">
            {section?.rows ? (
              <>
                <span><i className="legend-seat" /> 선택 가능</span>
                <span><i className="legend-seat legend-seat-selected" /> 고른 좌석</span>
                {memories.length ? <span><i className="legend-seat seat-dot-memory" /> 앉아봤어요</span> : null}
                <span><i className="legend-seat legend-seat-accessible">♿</i> 휠체어석</span>
              </>
            ) : (
              <span>구역을 고른 다음 티켓의 열·번호를 입력해요.</span>
            )}
          </div>
        </>
      )}

      <p className="notice">시설 좌표를 고른 것으로 이번 회차의 무대 구성까지 확인되지는 않아요. 공식 회차 안내와 티켓을 대조해 주세요.</p>
      <p className="map-provenance">{layout.source.label} · {layout.source.checkedAt} 확인 · 공식 원본은 링크로 대조하고, 선택 좌표는 접근 가능한 버튼으로 별도 재구성했어요.</p>
    </section>
  );
}

function ExactSeatRows({ section, selection, onSelectSeat, easy, memories }: {
  section: SeatLayoutSection;
  selection: SeatMapSelection | null;
  onSelectSeat(selection: SeatMapSelection): void;
  easy: boolean;
  memories: Array<{sectionCode:string;rowLabel:string;seatNumber:number;score:number|null}>;
}) {
  return (
    <div className="seat-map-scroll" tabIndex={0} aria-label={`${section.label} 좌석도. 좌우로 스크롤할 수 있어요.`}>
      <div className="seat-map-rows" data-position={section.position}>
        {section.rows?.map((row) => (
          <div className={`seat-map-row${row.gapBefore ? ' seat-map-row-aisle' : ''}`} key={row.label}>
            <span className="row-label">{row.label}열</span>
            <div className="seat-row-seats">
              {row.seats.map((seatNumber, seatIndex) => {
                const accessible = row.accessibleSeats?.includes(seatNumber) ?? false;
                const previousSeat = row.seats[seatIndex - 1];
                const hasAisleBefore = typeof previousSeat === 'number' && seatNumber - previousSeat > 1;
                const selected = selection?.sectionCode === section.label
                  && selection.rowLabel === row.label
                  && selection.seatNumber === seatNumber;
                const nearAisle = row.aisleAfter?.some((n)=>n===seatNumber || row.seats[row.seats.indexOf(n)+1]===seatNumber);
                const memory = memories.find((m)=>m.sectionCode===section.label&&m.rowLabel===row.label&&m.seatNumber===seatNumber);
                const seatLabel = `${section.label} ${row.label}열 ${seatNumber}번${accessible ? ' 휠체어석' : ''}${memory ? ' · 앉아봤어요' : ''}`;
                return (
                  <button
                    type="button"
                    className={`seat-dot${accessible ? ' seat-dot-accessible' : ''}${hasAisleBefore ? ' seat-dot-after-aisle' : ''}${easy && (accessible || nearAisle || row.gapBefore) ? ' seat-dot-easy' : ''}${memory ? ' seat-dot-memory' : ''}`}
                    aria-label={seatLabel}
                    aria-pressed={selected}
                    title={`${seatLabel}${memory && memory.score !== null ? ` · 시야 ${memory.score}` : ''}`}
                    data-memory-score={memory?.score}
                    key={seatNumber}
                    onClick={() => onSelectSeat({ sectionCode: section.label, rowLabel: row.label, seatNumber })}
                  >
                    {accessible ? <span aria-hidden="true">♿</span> : seatNumber}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionBlocks({ floor, selectedSectionId, onSelectSection }: {
  floor: SeatLayoutDefinition['floors'][number];
  selectedSectionId: string;
  onSelectSection(section: SeatLayoutSection): void;
}) {
  return (
    <div className="section-map" style={{ '--section-count': floor.sections.length } as CSSProperties}>
      {floor.sections.map((section) => (
        <button
          type="button"
          className="section-block"
          data-position={section.position}
          aria-pressed={selectedSectionId === section.id}
          key={section.id}
          onClick={() => onSelectSection(section)}
        >
          <span>{section.shortLabel}</span>
          <small>{section.seatCount ? `${section.seatCount.toLocaleString('ko-KR')}석` : '구역 선택'}</small>
          <span className="section-preview" aria-hidden="true">
            {Array.from({ length: 15 }, (_, index) => <i key={index} />)}
          </span>
        </button>
      ))}
    </div>
  );
}

function BookingSeatMap({ surfaceLabel }: { surfaceLabel: '무대' | '스크린' }) {
  return (
    <div className="booking-map" aria-label="공식 예매 회차에서 확인해야 하는 좌석도">
      <div className="venue-surface venue-surface-screen"><span>{surfaceLabel}</span></div>
      <div className="booking-seat-rows" aria-hidden="true">
        {[8, 10, 12, 12, 10].map((count, rowIndex) => (
          <div key={rowIndex}>
            {Array.from({ length: count }, (_, seatIndex) => <i key={seatIndex} />)}
          </div>
        ))}
      </div>
      <div className="booking-map-message">
        <strong>이 배치는 좌석 위치가 아니에요.</strong>
        <span>공식 예매에서 날짜·영화·회차를 고른 뒤 열리는 좌석도를 확인해 주세요.</span>
      </div>
    </div>
  );
}
